import { environment } from '@vivli/core/environments';
import { useStorage } from '@vivli/shared/infrastructure/hook';

const activeKeyStorageName = 'current-debug-key';

enum ErrorTypeEnum {
    All = 'all',
    Info = 'info',
    Error = 'error',
    Warn = 'warn',
    Debug = 'debug'
}

// Ordered least -> most verbose. An active key enables its own level plus every quieter level, so
// ?debug-key=debug shows the whole picture instead of hiding errors behind an exact-match check.
const levelOrder = [ErrorTypeEnum.Error, ErrorTypeEnum.Warn, ErrorTypeEnum.Info, ErrorTypeEnum.Debug];

const getActiveKey = (): string => {
    const { getItem, setItem } = useStorage();

    const params = new URLSearchParams(window.location.search);

    let key = params.get('debug-key');

    // logger bypass for local development
    if (environment.isLoggerEnabled) {
        key = 'all';
    }

    if (key) {
        setItem(activeKeyStorageName, key);
    } else {
        key = getItem<string>(activeKeyStorageName);
    }

    return key;
};

const isEnabled = (level: ErrorTypeEnum): boolean => {
    const activeKey = getActiveKey();
    if (activeKey === ErrorTypeEnum.All) {
        return true;
    }

    const activeIndex = levelOrder.indexOf(activeKey as ErrorTypeEnum);
    return activeIndex >= 0 && activeIndex >= levelOrder.indexOf(level);
};

export abstract class LoggerService {
    public static info = (message: any, ...optionalParams: any[]) => {
        if (isEnabled(ErrorTypeEnum.Info)) {
            console.info(message, ...optionalParams);
        }
    };

    public static error = (message: any, ...optionalParams: any[]) => {
        if (isEnabled(ErrorTypeEnum.Error)) {
            console.error(message, ...optionalParams);
        }
    };

    public static warn = (message: any, ...optionalParams: any[]) => {
        if (isEnabled(ErrorTypeEnum.Warn)) {
            console.warn(message, ...optionalParams);
        }
    };

    public static debug = (message: any, ...optionalParams: any[]) => {
        if (isEnabled(ErrorTypeEnum.Debug)) {
            console.debug(message, ...optionalParams);
        }
    };

    // Deliberately outside the level ladder above: `log` stays gated on 'all' only, so the existing
    // .log() call sites don't start emitting for anyone who sets ?debug-key=debug.
    public static log = (message: any, ...optionalParams: any[]) => {
        if (getActiveKey() === ErrorTypeEnum.All) {
            console.log(message, ...optionalParams);
        }
    };
}
